/**
 * Client for the per-user External-API connect flow (`/api/me/connections`).
 * Generic OAuth connect for any provider an admin configured under
 * Settings ▸ Features ▸ External APIs.
 */

import { api } from "@/utils/api-client";
import type { ConnectKind, ConnectionPurpose, SetupStep } from "./external-apis-service";

/** Whether the user can link now, or an admin must configure it first. */
export type ConnectionStatus = "connected" | "notConnected" | "notConfigured";

export type ConnectionEnvironment = "production" | "staging" | "development" | "sandbox";

export interface LinkedAccount {
  id: string;
  externalId: string;
  displayName?: string;
  accountLabel?: string;
  description?: string;
  environment?: ConnectionEnvironment;
  isDefault: boolean;
  scopes: string[];
  verifiedAt?: string;
}

export interface MeConnection {
  provider: string;
  displayName: string;
  logo?: string;
  authType: "oauth2";
  /** How the member links it (drives the connect UI). */
  connectKind: ConnectKind;
  /** identity | api | both. */
  purpose: ConnectionPurpose;
  /** connected | notConnected (linkable) | notConfigured (needs admin setup). */
  status: ConnectionStatus;
  connected: boolean;
  scopes: string[];
  /** Linked account's display name (e.g. GitHub login), when connected. */
  accountLabel?: string;
  /** All active accounts/API keys the user linked for this provider. */
  linkedAccounts: LinkedAccount[];
  /** Read-only "how to connect" steps, for member context. */
  setupSteps?: SetupStep[];
  /** Non-secret config the connect flow needs (e.g. Telegram `botUsername`). */
  config?: Record<string, string>;
}

/** Result of starting a connection — OAuth-like or instant/kind-specific. */
export interface StartConnectResult {
  authUrl?: string;
  state?: string;
  codeVerifier?: string;
  instant?: boolean;
  /** Kind-specific data to echo back to complete (e.g. phone OTP verificationId). */
  data?: Record<string, unknown>;
}

export interface ConnectionProfileInput {
  accountLabel?: string;
  description?: string;
  environment?: ConnectionEnvironment | "";
  isDefault?: boolean;
}

export const meConnectionsService = {
  async list(): Promise<MeConnection[]> {
    const res = (await api.get("/me/connections", { requiresAuth: true })) as {
      data?: { connections?: MeConnection[] };
    };
    return res?.data?.connections ?? [];
  },

  // These connection mutations all surface their own error to the user (the
  // OAuth hook + disconnect handler via toast, the connect/edit dialogs inline),
  // so the api-client's global error toast is suppressed here to avoid a
  // duplicate "already exists"-style toast firing alongside the caller's.

  /** Starts a connection. `payload` carries kind-specific start input (e.g. `{ phone }`). */
  async start(
    provider: string,
    payload?: Record<string, unknown>,
  ): Promise<StartConnectResult> {
    return api.post(`/me/connections/${provider}/start`, payload ?? {}, { requiresAuth: true, showErrorToast: false });
  },

  /** Completes a connection. `input` carries the kind-specific payload (code, fields, …). */
  async complete(
    provider: string,
    input: Record<string, unknown>,
  ): Promise<{ success: boolean }> {
    return api.post(`/me/connections/${provider}/complete`, input, { requiresAuth: true, showErrorToast: false });
  },

  async disconnect(provider: string): Promise<{ success: boolean }> {
    return api.delete(`/me/connections/${provider}`, { requiresAuth: true, showErrorToast: false });
  },

  async disconnectAccount(
    provider: string,
    identityId: string,
  ): Promise<{ success: boolean }> {
    return api.delete(`/me/connections/${provider}/accounts/${identityId}`, {
      requiresAuth: true,
      showErrorToast: false,
    });
  },

  async updateAccountProfile(
    provider: string,
    identityId: string,
    input: ConnectionProfileInput,
  ): Promise<{ success: boolean }> {
    return api.patch(`/me/connections/${provider}/accounts/${identityId}/profile`, input, {
      requiresAuth: true,
      showErrorToast: false,
    });
  },
};
